import React, { useRef, useEffect } from 'react';
import { MarkdownExtension, BoldExtension, ItalicExtension, HeadingExtension, BulletListExtension, OrderedListExtension, HistoryExtension } from 'remirror/extensions';
import { Remirror, EditorComponent, useRemirror, Toolbar, ComponentItem, ThemeProvider, useRemirrorContext } from '@remirror/react';
import { ToolbarGroupItem, ToolbarButtonItem } from '@remirror/react-components'
import type { ToolbarItemUnion } from "@remirror/react";
import { AllStyledComponent } from "@remirror/styles/emotion";
import { useHelpers } from '@remirror/react';
import { debounce } from 'lodash';

const extensions = () => [
    new BoldExtension({}),
    new ItalicExtension(),
    new HeadingExtension({ levels: [1, 2, 3] }),
    new BulletListExtension({}),
    new OrderedListExtension(),
    new MarkdownExtension({}),
    new HistoryExtension({}),
];

const toolbarItems: ToolbarItemUnion[] = [
    {
        type: ComponentItem.ToolbarGroup,
        label: "Simple Formatting",
        items: [
            { type: ComponentItem.ToolbarCommandButton, commandName: "toggleBold", display: "icon" },
            { type: ComponentItem.ToolbarCommandButton, commandName: "toggleItalic", display: "icon" },
        ],
        separator: "none",
    },
    {
        type: ComponentItem.ToolbarGroup,
        label: "Heading Formatting",
        items: [
            {
                type: ComponentItem.ToolbarCommandButton,
                commandName: "toggleHeading",
                display: "icon",
                attrs: { level: 1 },
            },
            {
                type: ComponentItem.ToolbarCommandButton,
                commandName: "toggleHeading",
                display: "icon",
                attrs: { level: 2 },
            },
            {
                type: ComponentItem.ToolbarCommandButton,
                commandName: "toggleHeading",
                display: "icon",
                attrs: { level: 3 },
            },
        ],
        separator: "none",
    },
    {
        type: ComponentItem.ToolbarGroup,
        label: "Lists",
        items: [
            {
                type: ComponentItem.ToolbarCommandButton,
                commandName: "toggleBulletList",
                display: "icon",
                attrs: { level: 1 },
            },
            {
                type: ComponentItem.ToolbarCommandButton,
                commandName: "toggleOrderedList",
                display: "icon",
                attrs: { level: 2 },
            },
        ],
        separator: "none",
    },
];

interface MarkdownPreviewProps {
    children?: never[];
    value: string;
    onChange: (value: string) => void;
    maxLength?: number;
}

// 1. Create a child component to handle logic inside the context
const MarkdownChangeHandler = ({ value, onChange, maxLength }: { value: string, onChange: (v: string) => void, maxLength?: number }) => {
    const { helpers, commands, addHandler, manager } = useRemirrorContext();

    useEffect(() => {
        // 2. Subscribe to changes using addHandler
        const dispose = addHandler('updated', () => {
             const md = helpers.getMarkdown();
             
             // Check length
             if (typeof maxLength === 'number' && md.length > maxLength) {
                 // Truncate the content
                 const truncated = md.slice(0, maxLength);
                 
                 // Force the editor to update to the truncated content
                 // OPTION 1: Check if it's on commands
                 if (commands.setContent) {
                     commands.setContent(truncated);
                 } 
                 // OPTION 2: Use the manager to directly create a new state from the string
                 else if (manager) {
                    manager.view.updateState(
                        manager.createState({
                            content: truncated,
                            stringHandler: 'markdown'
                        })
                    );
                 }
                 
                 // Propagate the truncated value to the parent
                 if (truncated !== value) {
                     onChange(truncated);
                 }
                 return;
             }

             // Propagate change normally
             if (md !== value) {
                 onChange(md);
             }
        });
        return dispose;
    }, [addHandler, helpers, commands, maxLength, value, onChange, manager]);

    return null;
};

export function MarkdownEditor({ value, onChange, maxLength = 3000 }: MarkdownPreviewProps) {

    const { manager, state } = useRemirror({
        extensions,
        content: value,
        stringHandler: "markdown"
    });

    return (
        <AllStyledComponent>
            <ThemeProvider>
                <Remirror 
                    manager={manager} 
                    initialContent={state} 
                    // 3. Removed 'onChange' from here because it lacks 'commands' access
                >
                    <Toolbar items={toolbarItems} refocusEditor label="Top Toolbar" />
                    <EditorComponent />
                    
                    {/* 4. Render the handler inside the provider */}
                    <MarkdownChangeHandler value={value} onChange={onChange} maxLength={maxLength} />
                </Remirror>
            </ThemeProvider>
        </AllStyledComponent>
    );
};
